import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { fetchOrganisations } from '../../services/organisations.service';
import { fetchOrgUsers } from '../../services/users.service';
import type { ReleaseNoteAudienceMode } from '../../services/releaseNotes.service';

interface OrgOption {
  _id: string;
  name: string;
}

interface UserOption {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  /** Used to drop selected users when their org leaves the selection. */
  orgId?: string;
}

export interface AudienceValue {
  mode: ReleaseNoteAudienceMode;
  orgIds: string[];
  userIds: string[];
}

interface Props {
  value: AudienceValue;
  onChange: (next: AudienceValue) => void;
  /** Resolved recipient count from the server; null while unknown. */
  audienceCount: number | null;
  disabled?: boolean;
}

/**
 * Three-mode recipient picker: everyone, whole organisations, or named users.
 *
 * The user list is scoped to the selected organisations (same cascade as
 * GlobalFilterBar) — picking users across every org at once would be an
 * unusable list at any real tenant count.
 */
const AudienceSelector: React.FC<Props> = ({
  value,
  onChange,
  audienceCount,
  disabled = false,
}) => {
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // GET /organisations/list responds with `{ organisations: [...] }`,
        // not a bare array and not the `{ data }` envelope other endpoints use.
        const res: any = await fetchOrganisations();
        setOrgs(Array.isArray(res) ? res : (res?.organisations ?? []));
      } catch {
        setOrgs([]);
      }
    })();
  }, []);

  // In `users` mode the admin picks orgs first to narrow the list, so this
  // cascade runs for that mode too.
  useEffect(() => {
    if (value.mode !== 'users') return;
    if (value.orgIds.length === 0) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    setIsLoadingUsers(true);
    (async () => {
      try {
        const res: any = await fetchOrgUsers(value.orgIds.join(','));
        if (!cancelled) setUsers(Array.isArray(res) ? res : (res?.data ?? []));
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setIsLoadingUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value.mode, value.orgIds]);

  const handleModeChange = (mode: ReleaseNoteAudienceMode) => {
    // Clear the other mode's selection so a stale org list can't silently widen
    // the audience after switching back.
    onChange({ mode, orgIds: [], userIds: [] });
  };

  const selectedOrgs = orgs.filter((o) => value.orgIds.includes(o._id));
  const selectedUsers = users.filter((u) => value.userIds.includes(u._id));

  const userLabel = (u: UserOption) =>
    `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
      ? `${`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()} (${u.email})`
      : u.email;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Audience
      </Typography>

      <FormControl disabled={disabled}>
        <RadioGroup
          row
          value={value.mode}
          onChange={(e) =>
            handleModeChange(e.target.value as ReleaseNoteAudienceMode)
          }
        >
          <FormControlLabel value="all" control={<Radio size="small" />} label="All users" />
          <FormControlLabel value="orgs" control={<Radio size="small" />} label="Selected organisations" />
          <FormControlLabel value="users" control={<Radio size="small" />} label="Selected users" />
        </RadioGroup>
      </FormControl>

      {(value.mode === 'orgs' || value.mode === 'users') && (
        <Autocomplete<OrgOption, true>
          multiple
          disabled={disabled}
          options={orgs}
          value={selectedOrgs}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a._id === b._id}
          onChange={(_, next) => {
            const nextOrgIds = next.map((o) => o._id);
            // Drop any already-selected user whose org just left the selection,
            // so the chips can't disagree with the org filter above them.
            const keptUserIds =
              value.mode === 'users'
                ? value.userIds.filter((id) => {
                    const user = users.find((u) => u._id === id);
                    return user?.orgId ? nextOrgIds.includes(user.orgId) : true;
                  })
                : value.userIds;
            onChange({ ...value, orgIds: nextOrgIds, userIds: keptUserIds });
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                size="small"
                label={option.name}
                {...getTagProps({ index })}
                key={option._id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={value.mode === 'users' ? 'Organisations (to narrow the user list)' : 'Organisations'}
              placeholder="Select organisations"
            />
          )}
          sx={{ mt: 1 }}
        />
      )}

      {value.mode === 'users' && (
        <Autocomplete<UserOption, true>
          multiple
          disabled={disabled || value.orgIds.length === 0}
          loading={isLoadingUsers}
          options={users}
          value={selectedUsers}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(a, b) => a._id === b._id}
          onChange={(_, next) =>
            onChange({ ...value, userIds: next.map((u) => u._id) })
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                size="small"
                label={
                  `${option.firstName ?? ''} ${option.lastName ?? ''}`.trim() ||
                  option.email
                }
                {...getTagProps({ index })}
                key={option._id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Users"
              placeholder={
                value.orgIds.length === 0
                  ? 'Select an organisation first'
                  : 'Select users'
              }
            />
          )}
          sx={{ mt: 1.5 }}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
        <GroupOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {audienceCount === null
            ? 'Calculating recipients…'
            : `${audienceCount} active ${audienceCount === 1 ? 'recipient' : 'recipients'}`}
        </Typography>
      </Box>
    </Box>
  );
};

export default AudienceSelector;
